import { create } from 'zustand';
import type { ID } from 'types';

export interface LotDuplicateQuantity {
  baselineGroupCount: number;
  liveGroupCount: number;
}

type DuplicateQuantityByLot = Record<string, LotDuplicateQuantity>;

interface TieredPricingState {
  clearLotDuplicateQuantity: (lotId: ID) => void;
  closePublishConfirm: () => void;
  dismissDuplicateQuantityWarning: () => void;
  duplicateQuantityByLot: DuplicateQuantityByLot;
  eventId: ID | null;
  isCheckingPublish: boolean;
  isConfirmingPublish: boolean;
  isDuplicateQuantityWarningDismissed: boolean;
  openPublishConfirm: () => void;
  resetDuplicateQuantityWarningDismissal: () => void;
  setCheckingPublish: (isCheckingPublish: boolean) => void;
  setLotDuplicateQuantity: (lotId: ID, counts: LotDuplicateQuantity) => void;
  syncEvent: (eventId: ID) => void;
}

const EVENT_DEFAULTS = {
  duplicateQuantityByLot: {},
  isCheckingPublish: false,
  isConfirmingPublish: false,
  isDuplicateQuantityWarningDismissed: false,
};

const sumLiveMinusBaseline = (byLot: DuplicateQuantityByLot): number => Object.values(byLot).reduce(
  (sum, { baselineGroupCount, liveGroupCount }) => sum + liveGroupCount - baselineGroupCount,
  0,
);

export const useTieredPricingStore = create<TieredPricingState>((set, get) => ({
  ...EVENT_DEFAULTS,
  clearLotDuplicateQuantity: lotId => {
    const key = String(lotId);
    const { duplicateQuantityByLot } = get();
    if (!(key in duplicateQuantityByLot)) return;
    set({ duplicateQuantityByLot: Object.fromEntries(
      Object.entries(duplicateQuantityByLot).filter(([id]) => id !== key),
    ) });
  },
  closePublishConfirm: () => set({ isConfirmingPublish: false }),
  dismissDuplicateQuantityWarning: () => set({ isDuplicateQuantityWarningDismissed: true }),
  eventId: null,
  openPublishConfirm: () => set({ isConfirmingPublish: true }),
  resetDuplicateQuantityWarningDismissal: () => set({ isDuplicateQuantityWarningDismissed: false }),
  setCheckingPublish: isCheckingPublish => set({ isCheckingPublish }),
  setLotDuplicateQuantity: (lotId, { baselineGroupCount, liveGroupCount }) => {
    const key = String(lotId);
    const { duplicateQuantityByLot } = get();
    const current = duplicateQuantityByLot[key];
    if (current?.baselineGroupCount === baselineGroupCount && current.liveGroupCount === liveGroupCount) return;
    set({ duplicateQuantityByLot: { ...duplicateQuantityByLot, [key]: { baselineGroupCount, liveGroupCount } } });
  },
  syncEvent: eventId => {
    if (get().eventId !== eventId) set({ ...EVENT_DEFAULTS, eventId });
  },
}));

export const useIsDuplicateQuantityWarningDismissed = (): boolean => (
  useTieredPricingStore(s => s.isDuplicateQuantityWarningDismissed)
);
export const useIsCheckingPublish = (): boolean => useTieredPricingStore(s => s.isCheckingPublish);
export const useIsConfirmingPublish = (): boolean => useTieredPricingStore(s => s.isConfirmingPublish);
/** Server count corrected by what the mounted grids see right now; a primitive, so it only re-renders on change. */
export const useDuplicateQuantityGroupCount = (serverGroupCount: number): number => useTieredPricingStore(
  s => Math.max(0, serverGroupCount + sumLiveMinusBaseline(s.duplicateQuantityByLot)),
);

export const tieredPricingStore = {
  clearLotDuplicateQuantity: (lotId: ID): void => useTieredPricingStore.getState().clearLotDuplicateQuantity(lotId),
  closePublishConfirm: (): void => useTieredPricingStore.getState().closePublishConfirm(),
  dismissDuplicateQuantityWarning: (): void => useTieredPricingStore.getState().dismissDuplicateQuantityWarning(),
  openPublishConfirm: (): void => useTieredPricingStore.getState().openPublishConfirm(),
  resetDuplicateQuantityWarningDismissal: (): void => (
    useTieredPricingStore.getState().resetDuplicateQuantityWarningDismissal()
  ),
  setCheckingPublish: (isCheckingPublish: boolean): void => (
    useTieredPricingStore.getState().setCheckingPublish(isCheckingPublish)
  ),
  setLotDuplicateQuantity: (lotId: ID, counts: LotDuplicateQuantity): void => (
    useTieredPricingStore.getState().setLotDuplicateQuantity(lotId, counts)
  ),
  syncEvent: (eventId: ID): void => useTieredPricingStore.getState().syncEvent(eventId),
};
